import { useMemo } from "react";
import type { FormValues } from "@/widgets/classic-form/form";
import { useFormAPI } from "@/widgets/classic-form/form";
import { FieldsGrid } from "@/features/fields-grid";
import { publishAddField, publishRemoveField } from "@/features/form-fields";
import type { Event } from "@/entities/data-model";
import { getEventInstancePath, getEventInstancesPath } from "@/entities/data-model";
import type { EventAssertion, EventFieldsGroup, Field } from "@/entities/form-metadata";
import { FieldType, getRowInstancesPaths } from "@/entities/form-metadata";
import type { FieldPath } from "@/shared/forms";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";
import { FieldMapper } from "./FieldMapper/FieldMapper";
import { AddNewEventFieldPanel } from "./Panel/AddNewEventFieldPanel";
import { YearRangeEventGroup } from "./YearRangeEventGroup";
import { resolveAddRowFocusTarget } from "./addRowFocusTarget";
import { EVENT_GRID_LAYOUT, YEAR_RANGE_GRID_LAYOUT } from "./gridLayout";
import type { BindableEventFieldsGroup, BindableEventFieldset } from "./rowPrimaryIdentity";
import { toBindableFieldsets } from "./rowPrimaryIdentity";

interface EventGroupFormFieldsProps {
    fieldGroup: EventFieldsGroup;
}

export const EventGroup: React.FC<EventGroupFormFieldsProps> = ({ fieldGroup }) => {
    const { assertions, fieldsetDisplayStrategy } = fieldGroup;
    const removable = fieldsetDisplayStrategy !== "prefilled-static";
    // A fieldset whose fields name no identity is dropped here, before either layout sees it.
    const bindableGroup = useMemo(
        (): BindableEventFieldsGroup => ({ ...fieldGroup, fieldsets: toBindableFieldsets(fieldGroup.fieldsets) }),
        [fieldGroup],
    );
    const { fieldsets } = bindableGroup;
    const { getFieldValue } = useFormAPI();
    const track = useTrackSearchFormInteraction();

    const coupledPathsOf = (fieldset: BindableEventFieldset, primaryPath: string) =>
        getRowInstancesPaths(fieldset).filter((path) => path !== primaryPath);

    const onRemoveItem = ({
        fieldName,
        index,
        fieldset,
    }: {
        fieldName: string;
        index: number;
        fieldset: BindableEventFieldset;
    }) => {
        track({ objectName: "remove_event" });
        publishRemoveField({ fieldName, index, coupledFieldNames: coupledPathsOf(fieldset, fieldName) });
    };

    const addNew = publishAddField;

    const dataAccessorFn = ({ primaryIdentity }: BindableEventFieldset, index: number) =>
        getEventInstancePath(primaryIdentity, index) as FieldPath<FormValues>;

    const formArrayAccessorFn = ({ primaryIdentity }: BindableEventFieldset) =>
        getEventInstancesPath(primaryIdentity) as FieldPath<FormValues>;

    // Each field names its own identity, so a mixed fieldset (e.g. Death date + Burial place) binds
    // each field to its own state entry rather than the fieldset's collapsed identity.
    const fieldDataToFieldPathFn = ({ field, rowIndex }: { field: Field<EventAssertion>; rowIndex: number }) =>
        getEventInstancePath(field, rowIndex);

    const handleItemAdded = (fieldset: BindableEventFieldset) => {
        const target = resolveAddRowFocusTarget(fieldset, assertions);
        if (!target) return;

        const { instancesPath, mappedAssertion } = target;
        const instances = (getFieldValue(instancesPath) as unknown as Array<Event>) || [];
        addNew({
            fieldName: instancesPath,
            coupledFieldNames: coupledPathsOf(fieldset, instancesPath),
            type: "event",
            newItemIndex: instances.length,
            mappedAssertion,
        });
    };

    const hasYearRange = useMemo(() => {
        for (const fieldset of fieldsets) {
            for (const field of fieldset.fields) {
                if (field.fieldType === FieldType.YearRange) {
                    return true;
                }
            }
        }
        return false;
    }, [fieldsets]);

    // When at least single field with YearRange found
    // Render a different layout altogether
    if (hasYearRange) {
        return (
            <YearRangeEventGroup
                fieldGroup={bindableGroup}
                FieldMapper={FieldMapper}
                gridConfig={{ Assertions: YEAR_RANGE_GRID_LAYOUT.assertions, Removable: removable }}
            />
        );
    }

    const controlsRow = <AddNewEventFieldPanel fieldsets={fieldsets} onItemAdded={handleItemAdded} />;

    return (
        <FieldsGrid<EventAssertion, BindableEventFieldset, BindableEventFieldsGroup>
            fieldGroup={bindableGroup}
            fieldSetToFormArrayPathFn={formArrayAccessorFn}
            fieldDataToFormFieldPathFn={dataAccessorFn}
            fieldDataToFieldPathFn={fieldDataToFieldPathFn}
            FieldMapper={FieldMapper}
            controlsRow={controlsRow}
            onRemoveItem={onRemoveItem}
            gridConfig={{
                FieldSetName: EVENT_GRID_LAYOUT.fieldsetLabel,
                Assertions: EVENT_GRID_LAYOUT.assertions,
                Removable: removable,
            }}
        />
    );
};
